import { useActiveSessionStore } from '@ctct/rise-app-context';
import { env } from '@ctct/rise-env';
import { genericLogger } from '@ctct/rise-logging';

import { getSops, ulid } from './helpers';

interface TrackingWindow extends Window {
  utag_data: Record<string, string>;
  utag: {
    link: (data: Record<string, unknown>) => void;
    view: (data: Record<string, unknown>) => void;
  };
  dataLayer: object[];
  QuantumMetricAPI: {
    sendEvent: (
      eventId: number,
      flag: number,
      value?: string | number | undefined,
      data?: object
    ) => void;
  };
}
declare let window: TrackingWindow;

const logger = genericLogger.child({ component: 'tracking' });

/**
 * Quantum Metric Event IDs
 * Those eventIDs need to be provided by the QM team
 * Please use #help-quantum-metric on Slack for any questions
 */
enum QM_EVENTS {
  NAVIGATOR_CLICK = 625,
  NAVIGATOR_VIEW = 626,
  NAVIGATOR_CLICK_WITHOUT_LINK = 627,
  CREATE_PANEL_INTERACTION = 628,
  CREATE_PANEL_CAMPAIGN_FILTER_TOGGLE = 629,
  CREATE_PANEL_CAMPAIGN_SELECTION = 630,
  CREATE_PANEL_NETWORK_FILTER_TOGGLE = 631,
  CLICK = 709,
}

/**
 * Quantum Metric Flags
 * https://thequad.quantummetric.com/kb/articles/654-quantum-metric-api
 */
enum QM_FLAGS {
  EVENT_FLAG_EVENT = 0,
  EVENT_FLAG_CONVERSION = 1,
  EVENT_FLAG_USER_LOGIN = 2,
  EVENT_FLAG_USER_EMAIL = 4,
  EVENT_FLAG_USER_FIRST_NAME = 8,
  EVENT_FLAG_USER_LAST_NAME = 16,
  EVENT_FLAG_USER_DATA = 32,
  EVENT_FLAG_CART_VALUE = 64,
  EVENT_FLAG_ABN = 128,
  EVENT_FLAG_ENCRYPT = 256,
}

const getPageName = () => {
  // If for some reason the data-pagename isn't set just pass the whole pathname as the page
  let pageName = window.location.pathname;
  const page = document.querySelector('[data-pagename]');
  if (page) {
    const dataPageName = page.getAttribute('data-pagename');
    if (dataPageName) pageName = dataPageName;
  } else {
    const corePage = document.querySelector('[data-tracking-pagename]');
    const corePageName = corePage?.getAttribute('data-tracking-pagename');
    if (corePageName) pageName = corePageName;
  }
  return pageName;
};

const getTrackingLabel = (eventAction: string, eventObject: string) => {
  return `BOS - ${getPageName()} - ${eventObject} - ${eventAction}`;
};

const getEventId = () => {
  try {
    return ulid();
  } catch (e) {
    logger.error(`Failed to generate event ID. Reason: ${e}`);
  }

  return '';
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- this is how gtag works
const gtag = function _gtag() {
  // eslint-disable-next-line prefer-rest-params -- this is how gtag works
  window?.dataLayer?.push(arguments);
};

/**
 * GA Event Tracking
 * @param eventAction action the user took
 * @param eventObject object the user interacted with
 */
const trackAction = (
  eventAction: string,
  eventObject: string,
  eventCategory = 'click',
  eventAttributes: Record<string, unknown> = {}
): void => {
  const { accountId, partnerId } = useActiveSessionStore.getState();
  const sops = getSops();
  const eventLabel = getTrackingLabel(eventAction, eventObject);
  const eventId = getEventId();

  const attributes = {
    ...eventAttributes,
    event_category: eventCategory,
    event_label: eventLabel,
    event_link_name: eventLabel,
    event_id: eventId,
  };

  // @ts-expect-error -- this is how gtag works
  gtag('event', eventCategory, {
    send_to: env('NEXT_PUBLIC_GA_TRACKING_ID'),
    page_title: getPageName(),
    SOID_hit: accountId,
    PUID_hit: partnerId,
    SOPS_hit: sops,
    ...attributes,
  });

  trackQM(QM_EVENTS.CLICK, QM_FLAGS.EVENT_FLAG_EVENT, eventLabel, { eventId });
};

/**
 * Tealium Event Tracking
 * @param eventType event type
 * @param eventLabel label of event
 * @param eventMetadata metadata of event
 * @param extraParams additional top-level variables to include in the data layer
 */
const trackMetadata = (
  eventType: string,
  eventLabel: string,
  eventMetadata: string,
  extraParams?: Record<string, string>
) => {
  // combined, gives up to 10s retry time
  const maxRetries = 20;
  const retryDelay = 500;
  let attemptCount = 0;
  const tryTrack = () => {
    // window.utag might not be available immediately
    if (window?.utag) {
      const { accountId, userId } = useActiveSessionStore.getState();
      window.utag.link({
        tealium_event: eventType,
        ga_event_category: eventType,
        ga_event_label: eventLabel,
        ga_event_action: eventMetadata,
        ...extraParams,
        accountID: accountId ?? undefined,
        userUuid: userId ?? undefined,
      });
    } else if (attemptCount < maxRetries) {
      attemptCount++;
      setTimeout(tryTrack, retryDelay);
    }
  };

  tryTrack();
};

/**
 * Quantum Metric Event Tracking
 * @param qmEventId event id
 * @param flag flag
 * @param value value
 */
const trackQM = (
  qmEventId: QM_EVENTS,
  flag = QM_FLAGS.EVENT_FLAG_EVENT,
  value?: number | string | undefined,
  data?: object | undefined
) => {
  const maxRetries = 20;
  const retryDelay = 500;
  let attemptCount = 0;
  const tryTrack = () => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- optional chaining does not work here
    if (typeof window !== 'undefined' && window.QuantumMetricAPI) {
      window.QuantumMetricAPI.sendEvent(qmEventId, flag, value, data);
    } else if (attemptCount < maxRetries) {
      attemptCount++;
      setTimeout(tryTrack, retryDelay);
    }
  };

  tryTrack();
};

export { getPageName, QM_EVENTS, trackAction, trackMetadata, trackQM };
